/**
 * `ErrorState` — `NEEDLE SKIP`. `docs/design-direction.md` §8.9.
 *
 * The only place `--warn` appears in the whole app. The word is set in Display 800 at
 * `--fs-28`, **torn in half by a jagged `clip-path`** with the two halves 2px out of
 * register in two inks, over a 1px-ruled `--bg-raised` plate carrying a catalogue-style
 * code in mono, one plain sentence of UI `--fs-15`, and a square outlined 44px retry
 * stamp. It occupies the same grid as the content it replaces, so nothing jumps when
 * the retry succeeds.
 *
 * ## Four decisions worth stating
 *
 * · **The tear is the misregistration.** Two clipped copies, 2px apart, in
 *   `--danger-text` and `--accent-quiet`. That costs no `mix-blend-mode` and does not
 *   spend the screen's single `[data-ghost]`, which the contract caps at one per
 *   screen (§6.3, §6.13) — so an error can be shown over a screen that already has
 *   its ghost without breaking the budget.
 * · **`NEEDLE SKIP` is `aria-hidden`.** It is English label print, and the UI language
 *   is Russian (§11). What a screen reader gets is the Russian sentence and the
 *   catalogue code, which is also what a user would read out over the phone.
 * · **The sentence comes from the failure, not from the call site.** `lib/api.ts` owns
 *   one plain Russian sentence per error code and the derived `ERR 0x..` — so two
 *   screens can never print two different messages, or two different codes, for one
 *   fault. Copy rule: say what happened and what to do, never apologise, never blame.
 * · **Retry is offered only when a retry can work.** `failure.retryable` is the whole
 *   condition. A button that re-runs a request the server has already refused is a lie
 *   about what is going to happen.
 */

import type { JSX } from 'react';

import type { ApiFailure } from '../../lib/api';
import { CropPlate } from '../../components/primitives/CropPlate';
import { cx } from '../../components/primitives/Rule';

import type { StateGrid } from './Loading';
import { StateFrame } from './Loading';
import styles from './states.module.css';

/** Real pressing-plant vocabulary: the needle jumping the groove (§8.9). */
export const NEEDLE_SKIP = 'NEEDLE SKIP';

/**
 * The retry control's copy.
 *
 * Sentence case for the same reason as everywhere else: the UI tier is where buttons
 * live and Cyrillic UI strings stay sentence-case, because tracked Cyrillic caps read
 * as shouting (§4.3, §12.16).
 */
export const RETRY_LABEL = 'Ещё раз';

export interface ErrorStateProps {
  /** The failure, from `lib/api.ts` or from `store/player`'s media classifier. */
  readonly failure: ApiFailure;
  /** The grid the content would have occupied. */
  readonly grid: StateGrid;
  /**
   * Re-runs whatever failed. The button appears only when this is given **and** the
   * failure is retryable.
   */
  readonly onRetry?: (() => void) | undefined;
  /**
   * An optional short mono phrase printed after the code, catalogue style
   * (`ERR 0x1A · Лента не читается`). Omitted entirely when absent — the plate never
   * prints a placeholder for a phrase it does not have (§12.6).
   */
  readonly cause?: string | undefined;
  readonly className?: string | undefined;
}

export function ErrorState({
  failure,
  grid,
  onRetry,
  cause,
  className,
}: ErrorStateProps): JSX.Element {
  const showRetry = onRetry !== undefined && failure.retryable;
  const hasCause = typeof cause === 'string' && cause.trim() !== '';

  return (
    <StateFrame grid={grid} state="error" className={className} role="alert">
      <p className={styles.tear} aria-hidden="true">
        <span className={cx('t-display', 't-28', styles.tearTop)}>{NEEDLE_SKIP}</span>
        <span className={cx('t-display', 't-28', styles.tearBottom)}>{NEEDLE_SKIP}</span>
      </p>

      <CropPlate>
        <div className={styles.plateBody}>
          <span className={cx('t-mono', 't-11', styles.code)} data-error-code="true">
            {hasCause ? `${failure.displayCode} · ${cause}` : failure.displayCode}
          </span>
          <p className={cx('t-ui', 't-15', styles.sentence)} data-state-string="error">
            {failure.message}
          </p>
        </div>
      </CropPlate>

      {showRetry ? (
        <button
          type="button"
          className={cx('t-ui', 't-17', styles.action)}
          onClick={onRetry}
          data-action="retry"
        >
          {RETRY_LABEL}
        </button>
      ) : null}
    </StateFrame>
  );
}
